import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'components/ui/toast';
import { useRemindInterventionRequest } from 'hooks/useInterventionRequests';
import { ApiError } from 'utils/unwrapResponse';
import type { InterventionRequest } from 'api/interventionRequestsApi';
import { technicianName } from 'pages/AdminIssues/components/upcomingAssignment';
import { nextReminderText } from 'pages/AdminIssues/components/solicitationRows';

/** The technician answered while the ops was reminding him (CA17). */
const remindNotPending = (error: unknown): boolean =>
  error instanceof ApiError && error.errorCode === 'INTERVENTION_REQUEST_NOT_PENDING';

/**
 * « Relancer » for both drawers: one reminder per double click (CA7), the verbatim toast, and on a
 * race (CA17) `refetch` instead of an alert. `remindingId` / `remindError` feed the rows.
 */
export const useRemindTechnician = (refetch: () => unknown) => {
  const { t } = useTranslation();
  const toast = useToast();
  const remind = useRemindInterventionRequest();
  // `isPending` lags a render behind the click; the ref is what makes a double click remind once.
  const inFlight = useRef(false);

  const remindTechnician = (request: InterventionRequest) => {
    if (inFlight.current) return;
    inFlight.current = true;
    remind.mutate(
      { issueKey: request.issueKey, serviceDeskId: request.serviceDeskId, requestId: request.id },
      {
        onSuccess: ({ data: reminded, notified }) => {
          const name = technicianName(reminded);
          // Only a queued job may claim « e-mail et notification » (M5); the reminder counts either way.
          if (!notified) {
            toast.error(t('Reminder recorded for {{name}}; the notification could not be sent.', { name }));
            return;
          }
          const sent = t('Reminder sent to {{name}} (e-mail and notification).', { name });
          toast.success(reminded.remindableAt ? `${sent} ${nextReminderText(t, reminded.remindableAt)}.` : sent);
        },
        onError: (error) => {
          if (remindNotPending(error)) refetch();
        },
        onSettled: () => {
          inFlight.current = false;
        },
      }
    );
  };

  return {
    remindTechnician,
    remindingId: remind.isPending ? (remind.variables?.requestId ?? null) : null,
    remindError: remindNotPending(remind.error) ? null : remind.error,
    reset: remind.reset,
  };
};
